import { useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button, Skeleton } from "@nexus/ui";
import { getUserFacingApiMessage, passThroughMessage } from "@/lib/api-client";
import { adminOpsSettingsQueryOptions, useSaveAdminOpsSettings } from "@/lib/queries";
import type { AdminOpsSettings } from "@/features/admin/api/admin-ops.api";
import { useIsSuperTenant, usePermissions } from "@/features/auth/hooks/usePermissions";
import { ChangelogNotice } from "@/features/changelog/components/shared/ChangelogNotice";
import { SettingsSaveFooter } from "@/features/changelog/components/shared/SettingsCard";
import {
  SettingsRail,
  settingsRailPanelId,
  settingsRailTabId,
} from "@/features/changelog/components/shared/SettingsRail";
import {
  changedOpsSettings,
  toOpsDraft,
  type AdminOpsSettingsDraft,
} from "@/features/admin/lib/admin-ops-settings";
import {
  groupsWithChanges,
  OPS_SETTINGS_GROUP_META,
  OPS_SETTINGS_GROUPS,
  type OpsSettingsGroup,
} from "@/features/admin/lib/ops-settings-groups";
import { BigQuerySettingsCard } from "./ops-settings/BigQuerySettingsCard";
import { CostSettingsCard } from "./ops-settings/CostSettingsCard";
import { EventsSettingsCard } from "./ops-settings/EventsSettingsCard";
import { ScheduledJobsSettingsCard } from "./ops-settings/ScheduledJobsSettingsCard";
import { TokenAlertsSettingsCard } from "./ops-settings/TokenAlertsSettingsCard";
import { TokenUsageSettingsCard } from "./ops-settings/TokenUsageSettingsCard";
import type { OpsSettingsCardProps } from "./ops-settings/types";

/** The card each rail entry opens. Keyed so a new group cannot forget one. */
const GROUP_CARDS: Record<OpsSettingsGroup, ComponentType<OpsSettingsCardProps>> = {
  events: EventsSettingsCard,
  jobs: ScheduledJobsSettingsCard,
  tokenUsage: TokenUsageSettingsCard,
  tokenAlerts: TokenAlertsSettingsCard,
  cost: CostSettingsCard,
  bigQuery: BigQuerySettingsCard,
};

/** Namespaces the rail's tab and panel ids. */
const RAIL_ID = "ops-settings";

interface AdminOpsSettingsPageProps {
  group: OpsSettingsGroup;
  onGroupChange: (group: OpsSettingsGroup) => void;
}

/**
 * How the Events, Scheduled jobs, and Token usage tabs behave.
 *
 * Some of these do real work on the server — the retention dials delete
 * history, the failure, spend, latency and budget alerts post to Slack — and
 * the rest set what the tabs show before anyone touches a filter.
 *
 * It is one saved record shown a group at a time. The draft and the save footer
 * therefore live here rather than in the cards: five unrelated groups sharing
 * one Save button is the whole reason the rail exists instead of five separate
 * pages, and it is what lets an edit survive a move between groups.
 */
export function AdminOpsSettingsPage({ group, onGroupChange }: AdminOpsSettingsPageProps) {
  const { t, i18n } = useTranslation();
  const isSuperTenant = useIsSuperTenant();
  const perms = usePermissions();
  const canView = isSuperTenant || perms.ADMIN_GET_OPS_SETTINGS.isOn;
  const canEdit = isSuperTenant || perms.ADMIN_UPDATE_OPS_SETTINGS.isOn;

  const { data, isPending, isError, refetch } = useQuery({
    ...adminOpsSettingsQueryOptions(),
    enabled: canView,
  });

  const save = useSaveAdminOpsSettings();

  const [draft, setDraft] = useState<AdminOpsSettingsDraft | null>(null);
  // Re-seeding during render rather than in an effect keeps the form and the
  // server copy in the same commit — same pattern as the changelog settings.
  const [baseline, setBaseline] = useState<AdminOpsSettings | null>(null);
  if (data && data !== baseline) {
    setBaseline(data);
    setDraft(toOpsDraft(data));
  }

  if (!canView) {
    return (
      <p className="m-0 max-w-[68ch] text-xs text-muted-foreground">
        {t("admin.opsSettings.noAccess", "You do not have access to operations settings.")}
      </p>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-start gap-3">
        <p className="m-0 text-sm text-muted-foreground">
          {t("admin.opsSettings.loadError", "Could not load operations settings.")}
        </p>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          {t("admin.opsSettings.retry", "Try again")}
        </Button>
      </div>
    );
  }

  if (isPending || !draft || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const changes = changedOpsSettings(draft, data);
  const changedCount = Object.keys(changes).length;
  const isReadOnly = !canEdit;

  function set<K extends keyof AdminOpsSettingsDraft>(key: K, value: AdminOpsSettingsDraft[K]) {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  }

  function handleSave() {
    if (changedCount === 0) return;
    save.mutate(changes, {
      onSuccess: () =>
        toast.success(t("admin.opsSettings.savedToast", "Operations settings saved")),
    });
  }

  const cardProps: OpsSettingsCardProps = {
    draft,
    set,
    isReadOnly,
    isSlackConfigured: data.slackConfigured,
  };
  const ActiveCard = GROUP_CARDS[group];
  const changedGroups = groupsWithChanges(Object.keys(changes));
  const railItems = OPS_SETTINGS_GROUPS.map((id) => ({
    id,
    icon: OPS_SETTINGS_GROUP_META[id].icon,
    label: t(OPS_SETTINGS_GROUP_META[id].labelKey, OPS_SETTINGS_GROUP_META[id].labelFallback),
    isChanged: changedGroups.has(id),
  }));

  const savedCredit =
    data.updatedAt && data.updatedByName
      ? t("admin.opsSettings.credit", "Last changed by {{by}} on {{at}}", {
          by: data.updatedByName,
          at: new Date(data.updatedAt).toLocaleDateString(i18n.language, {
            day: "numeric",
            month: "short",
            year: "numeric",
          }),
        })
      : t("admin.opsSettings.creditDefaults", "Nobody has changed these yet.");

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex min-h-0 flex-1 flex-col gap-4 md:flex-row md:gap-6">
        <SettingsRail
          idPrefix={RAIL_ID}
          label={t("admin.opsSettings.groups.label", "Operations settings groups")}
          unsavedLabel={t("admin.opsSettings.groups.unsaved", "Unsaved changes")}
          items={railItems}
          active={group}
          onChange={onGroupChange}
        />

        <div
          role="tabpanel"
          id={settingsRailPanelId(RAIL_ID, group)}
          aria-labelledby={settingsRailTabId(RAIL_ID, group)}
          className="min-h-0 flex-1 space-y-4 overflow-auto pb-2"
        >
          {isReadOnly ? (
            <ChangelogNotice
              title={t("admin.opsSettings.readOnlyTitle", "You can read these, not change them")}
              description={t(
                "admin.opsSettings.readOnly",
                "Ask an admin with the operations settings permission to make the change.",
              )}
            />
          ) : null}

          <ActiveCard {...cardProps} />
        </div>
      </div>

      {/* Kept out of the scroll area: a save button that scrolls away is a save
          button somebody does not press. It counts changes across every group,
          which is why the rail marks the groups they are sitting on. */}
      <SettingsSaveFooter
        status={
          save.error
            ? getUserFacingApiMessage(save.error, passThroughMessage)
            : changedCount > 0
              ? t("admin.opsSettings.dirty", { count: changedCount })
              : savedCredit
        }
      >
        {isReadOnly ? null : (
          <>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 rounded-sm text-xs"
              disabled={changedCount === 0 || save.isPending}
              onClick={() => {
                setDraft(toOpsDraft(data));
                save.reset();
              }}
            >
              {t("admin.opsSettings.discard", "Discard")}
            </Button>
            <Button
              size="sm"
              className="h-8 rounded-sm text-xs"
              disabled={changedCount === 0 || save.isPending}
              onClick={handleSave}
            >
              {save.isPending
                ? t("admin.opsSettings.saving", "Saving…")
                : t("admin.opsSettings.save", "Save changes")}
            </Button>
          </>
        )}
      </SettingsSaveFooter>
    </div>
  );
}
